import { HttpClient } from '@angular/common/http'
import { Injectable, Injector, OnDestroy } from '@angular/core'
import { LanguageService, TranslationResources } from '@spartacus/core'
import { InitOptions, i18n } from 'i18next'
import i18nextHttpBackend, { HttpBackendOptions, RequestCallback } from 'i18next-http-backend'
import { Subscription, combineLatest } from 'rxjs'
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators'
import { EbConfigInitializerService } from '../../config/config-initializer/eb-config-initializer.service'
import { EbBaseSiteService } from '../../site-context/services/eb-base-site.service'
import { EbI18nConfig } from '../config/eb-i18n-config'
import { EB_I18NEXT_INSTANCE } from './eb-i18next-instance'

export function ebI18nextInit(injector: Injector, serverRequestOrigin: string): () => Promise<any> {
  return () =>
    injector
      .get(EbConfigInitializerService)
      .getI18nStable()
      .pipe(
        tap((config: EbI18nConfig) => {
          let i18next = injector.get(EB_I18NEXT_INSTANCE)
          let i18nextConfig: InitOptions = {
            ns: [], // don't preload any namespaces
            fallbackLng: config.i18n?.fallbackLang,
            debug: config.i18n?.debug,
            interpolation: {
              escapeValue: false
            }
          }

          if (config.i18n?.backend?.loadPath) {
            i18next = i18next.use(i18nextHttpBackend)
            const loadPath = getLoadPath(config.i18n.backend.loadPath, serverRequestOrigin)
            const backend: HttpBackendOptions = {
              loadPath,
              request: ebI18nextGetHttpClient(
                injector.get(HttpClient),
                injector.get(EbBaseSiteService)
              )
            }
            i18nextConfig = { ...i18nextConfig, backend }
          }

          return i18next.init(i18nextConfig, () => {
            // Don't use i18next's 'resources' config key for adding static translations,
            // because it will disable loading chunks from backend. We add resources here, in the init's callback.
            if (!config.i18n?.skipDefaultTranslations) {
              i18nextAddTranslations(i18next, config.i18n?.resources)
            }
            injector
              .get(SiteContextI18nextSynchronizer)
              .init(i18next, injector.get(LanguageService), injector.get(EbBaseSiteService))
          })
        })
      )
      .toPromise()
}

export function i18nextAddTranslations(i18next: i18n, resources: TranslationResources = {}): void {
  Object.keys(resources).forEach(lang => {
    Object.keys(resources[lang]).forEach(chunkName => {
      i18next.addResourceBundle(lang, chunkName, resources[lang][chunkName], true, true)
    })
  })
}

@Injectable({ providedIn: 'root' })
export class SiteContextI18nextSynchronizer implements OnDestroy {
  sub: Subscription

  init(i18next: i18n, language: LanguageService, baseSiteService: EbBaseSiteService) {
    // always update language of i18next on site context (language) change
    this.sub =
      this.sub ??
      combineLatest([language.getActive(), baseSiteService.activeBaseSite$])
        .pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(current) === JSON.stringify(previous)
          })
        )
        .subscribe(([lang]) => i18next.changeLanguage(lang))
  }

  ngOnDestroy() {
    this.sub?.unsubscribe()
  }
}

/**
 * Returns a function appropriate for i18next to make http calls for JSON files.
 * See docs for `i18next-http-backend`: https://github.com/i18next/i18next-http-backend#backend-options
 *
 * It uses Angular HttpClient under the hood, so it works in SSR.
 * @param httpClient Angular http client
 */
export function ebI18nextGetHttpClient(
  httpClient: HttpClient,
  baseSiteService: EbBaseSiteService
): (
  options: HttpBackendOptions,
  url: string,
  payload: object | string,
  callback: RequestCallback
) => void {
  return (
    _options: HttpBackendOptions,
    url: string,
    _payload: object | string,
    callback: RequestCallback
  ) => {
    return baseSiteService
      .getActive()
      .pipe(
        distinctUntilChanged(),
        switchMap(baseSite => {
          return httpClient.get(url.replace('ebB2B-DEU', baseSite), {
            responseType: 'text'
          })
        })
      )
      .subscribe(
        data => callback(null, { status: 200, data }),
        error =>
          callback(error, {
            // a workaround for https://github.com/i18next/i18next-http-backend/issues/82
            data: null as any,
            status: error.status
          })
      )
  }
}
/**
 * Resolves the relative path to the absolute one in SSR, using the server request's origin.
 * It's needed, because Angular Universal doesn't support relative URLs in HttpClient. See Angular issues:
 * - https://github.com/angular/angular/issues/19224
 * - https://github.com/angular/universal/issues/858
 */
export function getLoadPath(path: string, serverRequestOrigin: string): string {
  if (serverRequestOrigin && !path.match(/^http(s)?:\/\//)) {
    if (path.startsWith('/')) {
      path = path.slice(1)
    }
    if (path.startsWith('./')) {
      path = path.slice(2)
    }
    return `${serverRequestOrigin}/${path}`
  }
  return path
}
